import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";

// Keep in sync with the backend PASSWORD_MIN_LENGTH setting.
const MIN_LENGTH = 10;

export function ChangePasswordPage() {
  const { refreshUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    // Checked here as well as on the server: telling the user immediately
    // beats a round trip to learn they mistyped the confirmation.
    if (next !== repeat) {
      setError("Nova lozinka i ponovljena lozinka nisu iste.");
      return;
    }

    setBusy(true);
    try {
      await api.post("/auth/password/change", {
        current_password: current,
        new_password: next,
      });
      setDone(true);
      setCurrent("");
      setNext("");
      setRepeat("");
      await refreshUser();
    } catch (caught) {
      setError(errorMessage(caught, "Promjena lozinke nije uspjela."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto flex max-w-md flex-col gap-4 px-4 py-10">
      <Link to="/" className="text-sm text-muted-foreground underline">
        ← Natrag
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Promjena lozinke</CardTitle>
          <CardDescription>
            Nova lozinka mora imati najmanje {MIN_LENGTH} znakova.
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          {error && <Alert variant="error">{error}</Alert>}
          {done && (
            <Alert variant="success">
              Lozinka je promijenjena. Ostale prijave na drugim uređajima su
              prekinute.
            </Alert>
          )}

          <form className="flex flex-col gap-4" onSubmit={submit}>
            <div className="flex flex-col gap-2">
              <Label htmlFor="current">Trenutna lozinka</Label>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                required
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="next">Nova lozinka</Label>
              <Input
                id="next"
                type="password"
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                required
                value={next}
                onChange={(event) => setNext(event.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="repeat">Ponovite novu lozinku</Label>
              <Input
                id="repeat"
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(event) => setRepeat(event.target.value)}
              />
            </div>

            <Button type="submit" disabled={busy}>
              {busy ? "Spremanje…" : "Promijeni lozinku"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {done && (
        <Link to="/" className={buttonVariants({ variant: "outline" })}>
          Natrag na početnu
        </Link>
      )}
    </main>
  );
}
